/* eslint-disable react-hooks/exhaustive-deps */
import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Grid, MenuItem, Stack } from "@mui/material";
import { FormTextField } from "components/atoms";
import {
  DetailTableTabs,
  DocumentDetailHeader,
  FormContent,
} from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  ICreateZoneRequest,
  IDeleteByIdRequest,
  IDownloadZoneDetailRequest,
  IGetByIdRequest,
  IUpdateZoneRequest,
} from "model";
import { EStatus } from "model/Type/EActivationStatus";
import { IZone } from "model/Zone/IZone";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { DownloadActions, ZoneActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { ZoneApiService } from "services";
import { EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import { LineOrder, Location, ProductCategory } from "./Components";

const validateSchema = yup.object().shape({
  zoneCode: yup
    .string()
    .length(3, I18nTranslator(translations.yupError.maxIs, { max: 3 }))
    .required(translations.yupError.required),
  zoneName: yup.string().required(translations.yupError.required),
  description: yup.string().required(translations.yupError.required),
});

const ZoneDetail = () => {
  const params = useParams<{ id: string; code: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isAdd = location.pathname.includes("new");
  const [request] = useState({
    id: params.id,
  });
  const { loading, data } = useRequest<IGetByIdRequest, IZone>(
    request,
    ZoneApiService.getZoneById,
    [],
    { holdRequest: isAdd }
  );
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const tabsName = [
    I18nTranslator(translations.masterData["line&way"]),
    I18nTranslator(translations.masterData.location),
    I18nTranslator(translations.masterData.productCategory),
  ];

  const _downloadZoneMasterData = () => {
    const params: IDownloadZoneDetailRequest = {
      ...request,
      zoneName: data?.zoneCode || "",
      pageSize: 999999,
      pageNumber: FIRST_PAGE,
      fileName: EXPORT_FILE_NAME.zoneMasterData,
    };
    dispatch(DownloadActions.downloadZoneDetailMasterData.request(params));
  };

  const tabsPanel = [
    <LineOrder />,
    <Location zoneId={params?.id} />,
    <ProductCategory />,
    // <StorageTemperature />,
  ];

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader />
      {!isAdd && (
        <DetailTableTabs
          tabsName={tabsName}
          tabsPanel={tabsPanel}
          headerButtons={
            <Stack direction={"row"}>
              <LoadingButton
                loading={downloading || loading}
                startIcon={<Download />}
                variant="contained"
                onClick={_downloadZoneMasterData}
              >
                {I18nTranslator(translations.button.exportZoneDetail)}
              </LoadingButton>
            </Stack>
          }
        />
      )}
    </div>
  );
};

const DetailHeader = () => {
  const params = useParams<{
    id: string;
  }>();
  const history = useHistory();
  const location = useLocation();
  const isAdd = location.pathname.includes("new");
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.ZoneReducer.action);
  const [request] = useState({
    id: params.id,
  });
  const { loading, data } = useRequest<IGetByIdRequest, IZone>(
    request,
    ZoneApiService.getZoneById,
    [],
    { holdRequest: isAdd }
  );

  const initialValues = isAdd
    ? ({
        zoneCode: "",
        zoneName: "",
        description: "",
        status: EStatus.ACTIVE,
      } as ICreateZoneRequest)
    : ({
        ...data,
        status: data?.status ? EStatus.ACTIVE : EStatus.IN_ACTIVE,
      } as IZone);
  const formik = useFormik<ICreateZoneRequest | IZone>({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: (
      values: ICreateZoneRequest | IZone,
      { validateForm }: FormikHelpers<any>
    ) => {
      validateForm().then(() => {
        const request: ICreateZoneRequest | IUpdateZoneRequest = {
          ...(values as IZone),
          status: values.status === EStatus.ACTIVE,
        };
        isAdd ? _createZone(request) : _updateZone(request);
      });
    },
  });

  const handleDeleteItem = () => {
    DialogHelper.openConfirmDeleteDialog({
      itemName: data?.zoneName,
      onDelete: () => _deleteZone({ id: data?.id! }),
    });
  };

  const handleGoBack = () => {
    if (
      action.includes(ZoneActions.deleteZone.successName) ||
      action.includes(ZoneActions.createZone.successName)
    ) {
      _clearAction();
      history.go(-1);
    }
  };

  const _createZone = (request: ICreateZoneRequest) => {
    dispatch(ZoneActions.createZone.request(request));
  };
  const _updateZone = (request: IUpdateZoneRequest) => {
    dispatch(ZoneActions.updateZone.request(request));
  };
  const _deleteZone = (request: IDeleteByIdRequest) => {
    dispatch(ZoneActions.deleteZone.request(request));
  };
  const _clearAction = () => {
    dispatch(ZoneActions.clearAction.request());
  };

  useEffect(() => {
    return () => {
      _clearAction();
    };
  }, []);

  useEffect(() => {
    handleGoBack();
  }, [action]);

  return (
    <DocumentDetailHeader
      title={I18nTranslator(translations.masterData.zoneDescription)}
      formik={formik}
      isAdd={isAdd}
      loading={
        action.includes(ZoneActions.updateZone.requestName) ||
        action.includes(ZoneActions.createZone.requestName) ||
        action.includes(ZoneActions.deleteZone.requestName)
      }
      onDelete={handleDeleteItem}
    >
      <FormContent
        title={I18nTranslator(translations.masterData.generaInformation)}
      >
        <Grid xs={6} item>
          <FormTextField
            required
            label={I18nTranslator(translations.masterData.zoneCode)}
            placeholder={I18nTranslator(translations.placeholder.zoneCode)}
            id="zoneCode"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            required
            label={I18nTranslator(translations.masterData.zoneName)}
            placeholder={I18nTranslator(translations.placeholder.zoneName)}
            id="zoneName"
          />
        </Grid>

        <Grid xs={6} item>
          <FormTextField
            required
            label={I18nTranslator(translations.masterData.zoneDescription)}
            fullWidth
            placeholder={I18nTranslator(
              translations.placeholder.zoneDescription
            )}
            id="description"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.masterData.status)}
            select
            id="status"
          >
            <MenuItem value={EStatus.ACTIVE}>
              {I18nTranslator(translations.masterData.active)}
            </MenuItem>
            <MenuItem value={EStatus.IN_ACTIVE}>
              {I18nTranslator(translations.masterData.inactive)}
            </MenuItem>
          </FormTextField>
        </Grid>
      </FormContent>
    </DocumentDetailHeader>
  );
};

export default ZoneDetail;
